import React, { useState, useRef, Fragment, createElement, Component } from 'react';
import { useClickOutside } from '../../../../app/hooks/useClickOutside';
import { AlertTriangle, AlertOctagon, Eye, XCircle, Info, Search, ChevronDown, Download, ShieldAlert } from 'lucide-react';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
import type { PipelineBatchResult, ValidationIssue, IssueDomain, SeverityLevel } from '../../../../governance';
import { deriveDisplaySeverity } from '../../../../governance';
import { ADMIN_MODULE_META } from '../../shared/moduleMetadata';
// ─── Constants ────────────────────────────────────────────────────────────────
export const MODULE_CONFIG = ADMIN_MODULE_META;
export const PIPELINE_STAGES: {
  name: string;
  label: string;
  domain: IssueDomain;
}[] = [{
  name: 'schemaStage',
  label: 'Schema Validatie',
  domain: 'schema'
}, {
  name: 'typeStage',
  label: 'Vraagtype Validatie',
  domain: 'type'
}, {
  name: 'contentStage',
  label: 'Inhoud & Taal',
  domain: 'content'
}, {
  name: 'pedagogyStage',
  label: 'Pedagogisch Contract',
  domain: 'pedagogy'
}, {
  name: 'supportStage',
  label: 'Hints & Uitleg',
  domain: 'support'
}, {
  name: 'familyStatsStage',
  label: 'Familie-statistieken',
  domain: 'familyStats'
}, {
  name: 'diversityStage',
  label: 'Vraagtype-diversiteit',
  domain: 'diversity'
}, {
  name: 'visualStage',
  label: 'Visuele Hulpmiddelen',
  domain: 'visual'
}, {
  name: 'graphStage',
  label: 'LDG-Graaf',
  domain: 'graph'
}, {
  name: 'seedStage',
  label: 'Seed Integriteit',
  domain: 'seed'
}];
/**
 * SEVERITY_CONFIG — Display config for the 5-level severity model (Phase 1).
 *
 * Keyed by `SeverityLevel` ('critical' | 'blocker' | 'review' | 'coach' | 'info').
 * Use `deriveDisplaySeverity(issue.kind, issue.severity)` to look up the
 * correct entry for a given ValidationIssue.
 */
export const SEVERITY_CONFIG: Record<SeverityLevel, {
  icon: typeof XCircle;
  bg: string;
  text: string;
  border: string;
  label: string;
}> = {
  critical: {
    icon: AlertOctagon,
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    label: 'Kritiek'
  },
  blocker: {
    icon: XCircle,
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
    label: 'Blocker'
  },
  review: {
    icon: Eye,
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    label: 'Review'
  },
  coach: {
    icon: AlertTriangle,
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    label: 'Verbeterpunt'
  },
  info: {
    icon: Info,
    bg: 'bg-slate-50',
    text: 'text-slate-700',
    border: 'border-slate-200',
    label: 'Info'
  }
};
// ─── Types ────────────────────────────────────────────────────────────────────
// NOTE (stab-ronde 4): `LogEntry` interface was removed — had zero callers
// across src/. Was only re-exported via the governance portal barrel.
// ─── Shared Components ────────────────────────────────────────────────────────
export function FilterBar({
  moduleFilter,
  onModuleFilter,
  search,
  onSearch,
  children
}: {
  moduleFilter: string;
  onModuleFilter: (v: string) => void;
  search: string;
  onSearch: (v: string) => void;
  children?: React.ReactNode;
}) {
  const moduleOptions = [{
    value: 'all',
    label: 'Alle domeinen'
  }, ...Object.entries(MODULE_CONFIG).map(([code, config]) => ({
    value: code,
    label: `${config.name} (${code})`
  }))];
  return <div className="flex flex-col sm:flex-row gap-3 bg-white p-3 sm:p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-4740">
      <div className="flex-1 w-full sm:max-w-sm" data-id="element-4741">
        <Input type="text" placeholder="Zoek op ID of code..." value={search} onChange={e => onSearch(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-4743" />} variant="admin" size="sm" data-id="element-4742" />
      </div>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2" data-id="element-4744">
        <Select value={moduleFilter} onChange={e => onModuleFilter(e.target.value)} options={moduleOptions} variant="admin" size="sm" fullWidth={false} data-id="element-4745" />
        {children}
      </div>
    </div>;
}
export function IssueRow({
  issue
}: {
  issue: ValidationIssue;
}) {
  // Phase 1: route via deriveDisplaySeverity so that high-severity blockers
  // render as 'Kritiek' and high-severity coach issues render as 'Review'.
  const displaySeverity = deriveDisplaySeverity(issue.kind, issue.severity);
  const sevConfig = SEVERITY_CONFIG[displaySeverity];
  const Icon = sevConfig.icon;
  return <div className={`flex items-start gap-3 p-3 rounded-lg border ${sevConfig.bg} ${sevConfig.border}`} data-id="element-4746">
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${sevConfig.text}`} data-id="element-4747" />
      <div className="min-w-0 flex-1" data-id="element-4748">
        <div className="flex items-center gap-2 mb-1" data-id="element-4749">
          <span className={`text-xs font-bold uppercase ${sevConfig.text}`} data-id="element-4750">
            {sevConfig.label}
          </span>
          <span className="text-[10px] font-mono text-gray-500 bg-white/50 px-1.5 py-0.5 rounded border border-gray-200/50" data-id="element-4751">
            {issue.code}
          </span>
          <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-4752">
            {issue.domain}
          </span>
        </div>
        <p className="text-sm text-gray-800 leading-snug" data-id="element-4753">{issue.message}</p>
        {issue.suggestion && <p className="text-xs text-gray-600 mt-1.5 flex items-start gap-1.5" data-id="element-4754">
            <span className="font-medium text-gray-700" data-id="element-4755">Suggestie:</span>
            {issue.suggestion}
          </p>}
        {issue.metadata && Object.keys(issue.metadata).length > 0 && <div className="mt-2 pt-2 border-t border-white/60" data-id="element-4756">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1" data-id="element-4757">
              Gestructureerde data
            </p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs" data-id="element-4758">
              {Object.entries(issue.metadata).map(([key, value]) => <Fragment key={key} data-id="element-4759">
                  <dt className="font-mono text-gray-500" data-id="element-4760">{key}:</dt>
                  <dd className="font-mono text-gray-800 break-words" data-id="element-4761">
                    {typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)}
                  </dd>
                </Fragment>)}
            </dl>
          </div>}
      </div>
    </div>;
}
// ─── Export Utilities ─────────────────────────────────────────────────────────
/**
 * Priority score for sorting: higher = more urgent to fix.
 *
 * Phase 1: Updated to weight by the 5-level display severity so that
 * a 'critical' (blocker + sev ≥ 90) outranks an ordinary 'blocker',
 * and 'review' (coach + sev ≥ 45) outranks plain 'coach'.
 */
function computePriority(r: PipelineBatchResult['results'][0]): number {
  let criticals = 0;
  let blockers = 0;
  let reviews = 0;
  let coaches = 0;
  let maxSeverity = 0;
  for (const i of r.gate.issues) {
    const ds = deriveDisplaySeverity(i.kind, i.severity);
    if (ds === 'critical') criticals++;else if (ds === 'blocker') blockers++;else if (ds === 'review') reviews++;else if (ds === 'coach') coaches++;
    if (i.severity > maxSeverity) maxSeverity = i.severity;
  }
  return criticals * 10000 + blockers * 1000 + reviews * 200 + coaches * 50 + maxSeverity + (100 - r.gate.score);
}
/** Sort results by priority: most problematic first */
function sortByPriority(results: PipelineBatchResult['results']): PipelineBatchResult['results'] {
  return [...results].sort((a, b) => computePriority(b) - computePriority(a));
}
/** Filter to only questions with blockers or multiple warnings */
function filterPriorityOnly(results: PipelineBatchResult['results']): PipelineBatchResult['results'] {
  return results.filter(r => {
    const blockers = r.gate.issues.filter(i => i.kind === 'blocker').length;
    const coaches = r.gate.issues.filter(i => i.kind === 'coach').length;
    return blockers > 0 || coaches >= 2;
  });
}
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
export function exportCSV(results: PipelineBatchResult['results'], filename: string, priorityOnly = false) {
  const sorted = sortByPriority(priorityOnly ? filterPriorityOnly(results) : results);
  const rows = [['Priority', 'Question ID', 'Family ID', 'Verdict', 'Score', 'Blockers', 'Warnings', 'Info', 'Total Issues', 'Domains', 'Top Issue Codes', 'Max Severity']];
  sorted.forEach((r, idx) => {
    const blockers = r.gate.issues.filter(i => i.kind === 'blocker');
    const coaches = r.gate.issues.filter(i => i.kind === 'coach');
    const infos = r.gate.issues.filter(i => i.kind === 'info');
    const domains = [...new Set(r.gate.issues.map(i => i.domain))].join('; ');
    const topCodes = r.gate.issues.sort((a, b) => b.severity - a.severity).slice(0, 3).map(i => i.code).join('; ');
    const maxSev = r.gate.issues.reduce((max, i) => Math.max(max, i.severity), 0);
    rows.push([String(idx + 1), r.questionId, r.familyId, r.gate.verdict, r.gate.score.toString(), blockers.length.toString(), coaches.length.toString(), infos.length.toString(), r.gate.issues.length.toString(), domains, topCodes, maxSev.toString()]);
  });
  // Escape CSV fields that might contain commas/semicolons
  const csv = rows.map(r => r.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n');
  downloadBlob(new Blob([csv], {
    type: 'text/csv'
  }), `${filename}.csv`);
}
export function exportJSON(results: PipelineBatchResult['results'], filename: string, meta?: Record<string, unknown>, priorityOnly = false) {
  const sorted = sortByPriority(priorityOnly ? filterPriorityOnly(results) : results);
  const data = {
    timestamp: new Date().toISOString(),
    exportType: priorityOnly ? 'priority' : 'full',
    totalExported: sorted.length,
    totalInPipeline: results.length,
    meta,
    summary: {
      blockerQuestions: sorted.filter(r => r.gate.issues.some(i => i.kind === 'blocker')).length,
      reviewQuestions: sorted.filter(r => r.gate.verdict === 'REVIEW_NEEDED').length,
      passQuestions: sorted.filter(r => r.gate.verdict === 'PASS').length
    },
    results: sorted.map((r, idx) => ({
      priority: idx + 1,
      priorityScore: computePriority(r),
      questionId: r.questionId,
      familyId: r.familyId,
      verdict: r.gate.verdict,
      score: r.gate.score,
      issues: r.gate.issues.sort((a, b) => b.severity - a.severity).map(i => ({
        code: i.code,
        kind: i.kind,
        domain: i.domain,
        severity: i.severity,
        message: i.message,
        suggestion: i.suggestion
      }))
    }))
  };
  downloadBlob(new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json'
  }), `${filename}.json`);
}
export function exportMarkdown(results: PipelineBatchResult['results'], filename: string, priorityOnly = false) {
  const sorted = sortByPriority(priorityOnly ? filterPriorityOnly(results) : results);
  const fails = sorted.filter(r => r.gate.verdict === 'FAIL');
  const reviews = sorted.filter(r => r.gate.verdict === 'REVIEW_NEEDED');
  const passes = sorted.filter(r => r.gate.verdict === 'PASS');
  let md = `# Governance Validatie Rapport${priorityOnly ? ' — Prioriteit' : ''}\n\n`;
  md += `Gegenereerd op: ${new Date().toLocaleString('nl-NL')}\n`;
  if (priorityOnly) {
    md += `\n> **Prioriteitsexport:** Alleen vragen met blockers of ≥2 verbeterpunten.\n`;
    md += `> ${sorted.length} van ${results.length} vragen geëxporteerd.\n`;
  }
  md += `\n## Samenvatting\n\n`;
  md += `| Status | Aantal |\n|---|---|\n`;
  md += `| 🔴 FAIL | ${fails.length} |\n`;
  md += `| 🟡 REVIEW | ${reviews.length} |\n`;
  md += `| 🟢 PASS | ${passes.length} |\n`;
  md += `| **Totaal** | **${sorted.length}** |\n\n`;
  // Domain breakdown
  const domainCounts: Record<string, number> = {};
  sorted.forEach(r => r.gate.issues.forEach(i => {
    domainCounts[i.domain] = (domainCounts[i.domain] ?? 0) + 1;
  }));
  if (Object.keys(domainCounts).length > 0) {
    md += `## Issues per Domein\n\n`;
    md += `| Domein | Aantal |\n|---|---|\n`;
    Object.entries(domainCounts).sort((a, b) => b[1] - a[1]).forEach(([domain, count]) => {
      md += `| ${domain} | ${count} |\n`;
    });
    md += '\n';
  }
  if (fails.length > 0) {
    md += `## 🔴 Geblokkeerde Vragen (FAIL)\n\n`;
    fails.forEach((r, idx) => {
      md += `### ${idx + 1}. \`${r.questionId}\` — Score: ${r.gate.score}%\n\n`;
      const issuesSorted = [...r.gate.issues].sort((a, b) => b.severity - a.severity);
      issuesSorted.forEach(i => {
        const icon = i.kind === 'blocker' ? '❌' : i.kind === 'coach' ? '⚠️' : 'ℹ️';
        md += `- ${icon} **[${i.domain.toUpperCase()}]** \`${i.code}\` (severity ${i.severity})\n  ${i.message}\n`;
        if (i.suggestion) md += `  → _${i.suggestion}_\n`;
      });
      md += '\n';
    });
  }
  if (reviews.length > 0) {
    md += `## 🟡 Aandacht Vereist (REVIEW)\n\n`;
    reviews.forEach((r, idx) => {
      md += `### ${idx + 1}. \`${r.questionId}\` — Score: ${r.gate.score}%\n\n`;
      const issuesSorted = [...r.gate.issues].sort((a, b) => b.severity - a.severity);
      issuesSorted.forEach(i => {
        const icon = i.kind === 'blocker' ? '❌' : i.kind === 'coach' ? '⚠️' : 'ℹ️';
        md += `- ${icon} **[${i.domain.toUpperCase()}]** \`${i.code}\` (severity ${i.severity})\n  ${i.message}\n`;
        if (i.suggestion) md += `  → _${i.suggestion}_\n`;
      });
      md += '\n';
    });
  }
  downloadBlob(new Blob([md], {
    type: 'text/markdown'
  }), `${filename}.md`);
}
export function ExportMenu({
  results,
  filePrefix,
  meta
}: {
  results: PipelineBatchResult['results'];
  filePrefix: string;
  meta?: Record<string, unknown>;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useClickOutside(menuRef, () => setOpen(false));
  const dateStr = new Date().toISOString().split('T')[0];
  const filename = `${filePrefix}-${dateStr}`;
  const priorityCount = filterPriorityOnly(results).length;
  return <div className="relative" ref={menuRef} data-id="element-4762">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-4763">
        <Download className="w-4 h-4" data-id="element-4764" />
        <span className="hidden sm:inline" data-id="element-4765">Exporteer</span>
        <ChevronDown className="w-3.5 h-3.5 ml-0.5 text-gray-400" data-id="element-4766" />
      </button>
      {open && <div className="absolute right-0 mt-1 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20" data-id="element-4767">
          {/* Priority exports */}
          <div className="px-3 py-1.5 border-b border-gray-100" data-id="element-4768">
            <p className="text-[10px] font-bold uppercase tracking-wider text-red-600" data-id="element-4769">
              Prioriteit ({priorityCount} vragen)
            </p>
          </div>
          <button onClick={() => {
        exportCSV(results, `${filename}-prioriteit`, true);
        setOpen(false);
      }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center gap-2" data-id="element-4770">
            <ShieldAlert className="w-3.5 h-3.5 text-red-500" data-id="element-4771" />
            Prioriteit CSV
          </button>
          <button onClick={() => {
        exportJSON(results, `${filename}-prioriteit`, meta, true);
        setOpen(false);
      }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center gap-2" data-id="element-4772">
            <ShieldAlert className="w-3.5 h-3.5 text-red-500" data-id="element-4773" />
            Prioriteit JSON
          </button>
          <button onClick={() => {
        exportMarkdown(results, `${filename}-prioriteit`, true);
        setOpen(false);
      }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center gap-2" data-id="element-4774">
            <ShieldAlert className="w-3.5 h-3.5 text-red-500" data-id="element-4775" />
            Prioriteit Markdown
          </button>

          {/* Full exports */}
          <div className="px-3 py-1.5 border-b border-t border-gray-100 mt-1" data-id="element-4776">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-4777">
              Volledig ({results.length} vragen)
            </p>
          </div>
          <button onClick={() => {
        exportCSV(results, filename);
        setOpen(false);
      }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors" data-id="element-4778">
            Exporteer als CSV
          </button>
          <button onClick={() => {
        exportJSON(results, filename, meta);
        setOpen(false);
      }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors" data-id="element-4779">
            Exporteer als JSON
          </button>
          <button onClick={() => {
        exportMarkdown(results, filename);
        setOpen(false);
      }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors" data-id="element-4780">
            Exporteer als Markdown
          </button>
        </div>}
    </div>;
}